import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Compass, TrendingUp, MessageSquareQuote, ArrowUpRight } from "lucide-react";

const services = [
  {
    n: "01",
    icon: Compass,
    title: "Leadership & Mindset Coaching",
    description:
      "For leaders and high-performers who need clarity, discipline, confidence, and better personal systems.",
    bullets: [
      "Executive 1:1 coaching",
      "Mindset, identity & habits",
      "Decision frameworks & focus",
      "Personal operating systems",
    ],
  },
  {
    n: "02",
    icon: TrendingUp,
    title: "Business & Growth Strategy",
    description:
      "For businesses, nonprofits, and entrepreneurs who need help with positioning, revenue, fundraising, partnerships, and execution.",
    bullets: [
      "Positioning & messaging",
      "Revenue & growth strategy",
      "Fundraising & partnerships",
      "Operating cadence & execution",
    ],
  },
  {
    n: "03",
    icon: MessageSquareQuote,
    title: "Communications & Brand Strategy",
    description:
      "For people or organizations that need sharper messaging, stronger storytelling, better campaigns, and a more compelling presence.",
    bullets: [
      "Narrative & brand voice",
      "Campaigns & launches",
      "Executive communication",
      "Story-driven content systems",
    ],
  },
];

export const Services = () => {
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 md:py-32 bg-[#F7F6F3]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-16 md:mb-20">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="ssg-hero-rule" />
              <span className="text-[11px] tracking-[0.28em] uppercase font-bold text-[#9B1C1C]">
                Consulting Tracks
              </span>
            </div>
            <h2
              data-testid="services-heading"
              className="font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-neutral-950 leading-[1.02]"
            >
              Sharpen the message.<br />
              Strengthen the systems.<br />
              <span className="italic font-light">Move with purpose.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[17px] text-neutral-600 leading-relaxed font-medium max-w-md">
              Three focused engagements that orbit the Performance operating
              system — available on their own, or layered in when the moment
              calls for deeper strategic work.
            </p>
            <Link
              to="/strategic-consulting"
              data-testid="services-consulting-link"
              className="mt-6 inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-bold text-neutral-950 ssg-link-underline"
            >
              Explore Strategic Consulting
              <ArrowUpRight size={14} strokeWidth={1.6} />
            </Link>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-px bg-neutral-200">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                data-testid={`service-card-${i}`}
                className="bg-white p-10 lg:p-12 group relative flex flex-col h-full transition-all duration-500 hover:bg-neutral-950"
              >
                <div className="flex items-start justify-between mb-10">
                  <span className="font-display text-sm font-bold tracking-[0.2em] text-[#9B1C1C] group-hover:text-[#E6B0B0] transition-colors">
                    /{s.n}
                  </span>
                  <Icon
                    size={28}
                    strokeWidth={1.4}
                    className="text-neutral-900 group-hover:text-white transition-colors"
                  />
                </div>

                <h3 className="font-display text-2xl lg:text-[26px] font-bold tracking-tight text-neutral-950 group-hover:text-white leading-snug transition-colors">
                  {s.title}
                </h3>

                <p className="mt-5 text-[15px] leading-relaxed text-neutral-600 group-hover:text-neutral-300 font-medium transition-colors">
                  {s.description}
                </p>

                <ul className="mt-8 space-y-3 flex-1">
                  {s.bullets.map((b) => (
                    <li
                      key={b}
                      className="flex items-start gap-3 text-[13px] tracking-wide text-neutral-700 group-hover:text-neutral-300 transition-colors"
                    >
                      <span className="mt-2 w-3 h-px bg-[#9B1C1C] flex-none" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => go("contact")}
                  data-testid={`service-cta-${i}`}
                  className="mt-10 inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-bold text-neutral-950 group-hover:text-white transition-colors"
                >
                  Start a conversation
                  <ArrowUpRight
                    size={14}
                    strokeWidth={1.6}
                    className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                  />
                </button>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
